<template>
	<v-dialog v-model="localDialogOpen" width="480px" persistent>
		<v-card color="rgba(41, 41, 47, 0.8)">
			<v-card-title class="text-h6">{{ getTitleKey() }}</v-card-title>
			<v-card-text>
				<v-row class="ma-0">
					<v-col cols="12">
						<!-- Name -->
						<SolidMeshPropertyFormTable transparent>
							<tr>
								<td>{{ $t("NAME") }}</td>
								<td>
									<v-text-field v-model="formData.name" variant="underlined" density="compact"
										:rules="[v => !!(v && v.trim() !== '')]" hide-details />
								</td>
							</tr>
						</SolidMeshPropertyFormTable>
						<!-- Size -->
						<SolidMeshPropertyFormTable transparent>
							<!-- Pipe Parameters -->
							<template v-if="selectedStructureType === getSimpleStructureType.PIPE">
								<tr>
									<td>{{ $t("PIPE_DIAMETER") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.pipe.diameter" base-unit="mm" variant="underlined"
											density="compact" :step="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE" :decimal-length="DIMENSION_DECIMALS"
											hide-details />
									</td>
								</tr>
								<tr>
									<td>{{ $t("PIPE_LENGTH") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.pipe.length" variant="underlined" density="compact"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" hide-details />
									</td>
								</tr>
								<tr>
									<td>{{ $t('GRADIENT') }}</td>
									<td>
										<SlopeSettingInput variant="underlined"
											:suffix-to-display="getSuffix($store.state.roadParameterSlopeUnit.longitudinalSlope)"
											:is-editable="true" v-model.number="formData.pipe.gradient.value"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:decimal-length="resolveMaxDecimals(SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE)"
											:max-value="SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE"
											:min-value="-SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE" :un-valid-zero="true"
											@update:display-value="onPipeGradientDisplayValueChange"
											@update:model-value="onPipeGradientChange" />
									</td>
								</tr>
							</template>
							<!-- Retaining Wall Parameters -->
							<template v-if="selectedStructureType === getSimpleStructureType.RETAINING_WALL">
								<tr>
									<td>{{ $t("WALL_WIDTH") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.retainingWall.width" variant="underlined" density="compact"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" hide-details />
									</td>
								</tr>
								<tr>
									<td>{{ $t("WALL_HEIGHT") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.retainingWall.height" variant="underlined" density="compact"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" hide-details />
									</td>
								</tr>
								<tr>
									<td>{{ $t('GRADIENT') }}</td>
									<td>
										<SlopeSettingInput variant="underlined"
											:suffix-to-display="getSuffix($store.state.roadParameterSlopeUnit.longitudinalSlope)"
											:is-editable="true" v-model.number="formData.retainingWall.gradient.value"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:decimal-length="resolveMaxDecimals(SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE)"
											:max-value="SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE"
											:min-value="-SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE" :un-valid-zero="true"
											@update:display-value="onRetainingWallGradientDisplayValueChange" />
									</td>
								</tr>
								<!-- Thickness: hidden by default; enable with window.debugFlag.wallThickness = true -->
								<tr v-if="debugWallThickness">
									<td>{{ $t("WALL_THICKNESS") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.retainingWall.thickness" variant="underlined"
											density="compact" :step="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:min-value="SIMPLE_STRUCTURE_DIMENSION_MIN" :max-value="SIMPLE_STRUCTURE_DIMENSION_MAX"
											:decimal-length="DIMENSION_DECIMALS" hide-details />
									</td>
								</tr>
							</template>
						</SolidMeshPropertyFormTable>
						<!-- Control points (two-point) -->
						<SolidMeshPropertyFormTable transparent>
							<!-- Basis point 1 -->
							<tr @mouseenter="hoveredP1 = true" @mouseleave="hoveredP1 = false">
								<td :rowspan="selectedStructureType === getSimpleStructureType.PIPE ? 3 : 2"
									:class="{ 'row-title': true, 'highlight-title': hoveredP1 }">{{ $t(getBasisPointKey(1))
									}}
								</td>
								<td class="coordinate-label">{{ $t("X") }}</td>
								<td>
									<DistanceSettingInput v-model="pointsObj.p1.x" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
							<tr @mouseenter="hoveredP1 = true" @mouseleave="hoveredP1 = false">
								<td class="coordinate-label">{{ $t("Y") }}</td>
								<td>
									<DistanceSettingInput v-model="pointsObj.p1.y" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
							<tr v-if="selectedStructureType === getSimpleStructureType.PIPE" @mouseenter="hoveredP1 = true"
								@mouseleave="hoveredP1 = false">
								<td class="coordinate-label">{{ $t("Z") }}</td>
								<td>
									<DistanceSettingInput v-model="pointsObj.p1.z" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
							<!-- Basis point 2 -->
							<tr @mouseenter="hoveredP2 = true" @mouseleave="hoveredP2 = false">
								<td :rowspan="selectedStructureType === getSimpleStructureType.PIPE ? 3 : 2"
									:class="{ 'row-title': true, 'highlight-title': hoveredP2 }">{{ $t(getBasisPointKey(2))
									}}
								</td>
								<td class="coordinate-label">{{ $t("X") }}</td>
								<td>
									<DistanceSettingInput v-model="pointsObj.p2.x" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
							<tr @mouseenter="hoveredP2 = true" @mouseleave="hoveredP2 = false">
								<td class="coordinate-label">{{ $t("Y") }}</td>
								<td>
									<DistanceSettingInput v-model="pointsObj.p2.y" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
							<tr v-if="selectedStructureType === getSimpleStructureType.PIPE" @mouseenter="hoveredP2 = true"
								@mouseleave="hoveredP2 = false">
								<td class="coordinate-label">{{ $t("Z") }}</td>
								<td>
									<DistanceSettingInput v-model="pointsObj.p2.z" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
						</SolidMeshPropertyFormTable>
						<!-- Color -->
						<SolidMeshPropertyFormTable transparent>
							<tr>
								<td>{{ $t("COLOR") }}</td>
								<td>
									<v-menu :close-on-content-click="false" location="bottom start">
										<template v-slot:activator="{ props }">
											<v-avatar :color="rgbToHex(formData.color)" size="20px" v-bind="props" />
										</template>
										<v-card min-width="300">
											<v-color-picker v-model="pickerColor" mode="rgb" hide-inputs show-swatches
												@update:model-value="onPickerColorChange" />
										</v-card>
									</v-menu>
								</td>
							</tr>
						</SolidMeshPropertyFormTable>
					</v-col>
				</v-row>
			</v-card-text>
			<v-card-actions class="justify-end pa-4">
				<v-btn variant="flat" @click="cancel" color="red" size="small">
					{{ $t("CANCEL") }}
				</v-btn>
				<v-btn variant="flat" @click="confirm" :color="isFormValid ? 'primary' : 'grey'" :disabled="!isFormValid"
					size="small">
					{{ $t("ADD") }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import SlopeSettingInput from "@/components/Project/SlopeSettingInput.vue";
import { UnitTypes } from "@/constant.js";
import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "@/utils/googleAnalytics.js";
import {
	buildPipeFromTwoPoints,
	buildRetainingWallFromTwoPoints,
	scaleSegmentToLength,
} from "earthbrain-viewer";
import DistanceSettingInput from "../DistanceSettingInput.vue";
import { SIMPLE_STRUCTURE_TYPES } from "./SimpleStructureTypeConfig";
import SolidMeshPropertyFormTable from "./SolidMeshPropertyFormTable.vue";
import {
	computePipeGradientFromPoints,
	computePipeP2FromGradient,
	roundPointTo3,
	roundTo3,
	slopeValueToGradientPercent,
} from "./composables/useDoublePointFormSync";
import { DIMENSION_DECIMALS } from "./composables/useSolidMeshDimensionConfig";
import {
	SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE,
	SIMPLE_STRUCTURE_DIMENSION_MAX,
	SIMPLE_STRUCTURE_DIMENSION_MIN,
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS,
	getMaxDecimals,
	toOptionalNumber,
} from "./composables/useSolidMeshEdit";
import { useSolidMeshUnitDisplay } from "./composables/useSolidMeshUnitDisplay";

/** Metric creation defaults. Feet sites override them via `creationDefaults()`. */
const METRIC_CREATION_DEFAULTS = {
	pipeDiameter: "300.00", // mm
	retainingWallHeight: "1.00", // m
};

function createInitialFormData(defaults = METRIC_CREATION_DEFAULTS) {
	return {
		name: "",
		color: { r: 1, g: 1, b: 1, a: 1 },
		pipe: {
			diameter: defaults.pipeDiameter, // mm
			length: "0.00", // m
			gradient: { mode: "percent", value: 0 },
			points: { p1: { x: 0, y: 0, z: 0 }, p2: { x: 0, y: 0, z: 0 } },
		},
		retainingWall: {
			width: "0.00",
			height: defaults.retainingWallHeight,
			thickness: "0.50", // m (fixed 0.5m per spec)
			gradient: { mode: "percent", value: 0 },
			points: { p1: { x: 0, y: 0, z: 0 }, p2: { x: 0, y: 0, z: 0 } },
		},
	};
}

export default {
	name: "PropertyDialogSimpleStructureDoublePoint",
	components: {
		SlopeSettingInput,
		SolidMeshPropertyFormTable,
		DistanceSettingInput,
	},
	setup() {
		// DistanceSettingInput owns the unit conversion; only the decimal count
		// and the creation defaults have to be resolved here.
		const { isMetricDistance, convertLength } = useSolidMeshUnitDisplay();
		return { isMetricDistance, convertLength };
	},
	props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
		selectedStructureType: {
			type: String,
			default: null,
		},
		/** Two-point initial coords — { p1: {x,y,z}, p2: {x,y,z} } */
		initialTwoPoints: {
			type: Object,
			default: null,
		},
		tempMeshId: {
			type: String,
			default: null,
		},
	},
	emits: ["confirm", "cancel", "update:modelValue"],
	data() {
		return {
			formData: createInitialFormData(this.creationDefaults()),
			gradientDisplayValues: {
				pipe: "0",
				retainingWall: "0",
			},
			localDialogOpen: false,
			suppressSync: false,
			hoveredP1: false,
			hoveredP2: false,
			previewUpdateTimer: null,
			retainingWallBaseZ: { p1: 0, p2: 0 },
		};
	},
	computed: {
		DIMENSION_DECIMALS() {
			return DIMENSION_DECIMALS;
		},
		anchorPointDecimals() {
			return SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.decimalLength;
		},
		SIMPLE_STRUCTURE_DIMENSION_MIN() {
			return SIMPLE_STRUCTURE_DIMENSION_MIN;
		},
		SIMPLE_STRUCTURE_DIMENSION_MAX() {
			return SIMPLE_STRUCTURE_DIMENSION_MAX;
		},
		SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE() {
			return SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE;
		},
		anchorPointStep() {
			return SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.step;
		},
		getSimpleStructureType() {
			return SIMPLE_STRUCTURE_TYPE;
		},
		debugWallThickness() {
			return !!window.debugFlag?.wallThickness;
		},
		isFormValid() {
			if (this.formData.name.trim() === "") return false;
			const p1 = this.pointsObj.p1;
			const p2 = this.pointsObj.p2;
			const dimensionMax = SIMPLE_STRUCTURE_DIMENSION_MAX;
			const dimensionMax_mm = SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE;
			if (
				![p1.x, p1.y, p1.z, p2.x, p2.y, p2.z].every(this.isNonEmptyFiniteNumber)
			)
				return false;
			switch (this.selectedStructureType) {
				case SIMPLE_STRUCTURE_TYPE.PIPE:
					return (
						this.formData.pipe.diameter > 0 &&
						this.formData.pipe.length > 0 &&
						this.formData.pipe.length <= dimensionMax &&
						this.hasAllowedDecimals(
							this.gradientDisplayValues.pipe,
							dimensionMax_mm,
						)
					);
				case SIMPLE_STRUCTURE_TYPE.RETAINING_WALL:
					return (
						this.formData.retainingWall.width > 0 &&
						this.formData.retainingWall.width <= dimensionMax &&
						this.formData.retainingWall.height > 0 &&
						this.formData.retainingWall.height <= dimensionMax &&
						this.hasAllowedDecimals(
							this.gradientDisplayValues.retainingWall,
							dimensionMax_mm,
						)
					);
				default:
					return false;
			}
		},
		pointsObj() {
			const key = this.getPointsKey();
			if (key && this.formData[key] && this.formData[key].points)
				return this.formData[key].points;
			return { p1: { x: 0, y: 0, z: 0 }, p2: { x: 0, y: 0, z: 0 } };
		},
		pickerColor: {
			get() {
				return {
					r: Math.round((this.formData.color.r ?? 1) * 255),
					g: Math.round((this.formData.color.g ?? 1) * 255),
					b: Math.round((this.formData.color.b ?? 1) * 255),
				};
			},
			set(_) {},
		},
	},
	watch: {
		localDialogOpen(newVal) {
			if (newVal === false) {
				this.$emit("update:modelValue", false);
			}
		},
		"formData.pipe.points": {
			handler() {
				if (this.suppressSync) return;
				this.suppressSync = true;
				const p1 = this.formData.pipe.points.p1;
				const p2 = this.formData.pipe.points.p2;
				const { gradientValue, length } = computePipeGradientFromPoints(p1, p2);
				this.formData.pipe.length = length;
				this.formData.pipe.gradient.value = gradientValue;
				this.$nextTick(() => {
					this.suppressSync = false;
				});
				if (this.tempMeshId) this.schedulePreviewUpdate();
			},
			deep: true,
		},
		"formData.pipe.length"(newVal) {
			if (this.suppressSync) return;
			this.suppressSync = true;
			const p1 = this.formData.pipe.points.p1;
			const p2 = this.formData.pipe.points.p2;
			const newLen = Number(newVal) || 0;
			const newP2 = scaleSegmentToLength(p1, p2, newLen, "cylinder");
			this.formData.pipe.points.p2 = newP2;
			this.$nextTick(() => {
				this.suppressSync = false;
			});
			if (this.tempMeshId) this.schedulePreviewUpdate();
		},
		"formData.pipe.points.p1.z"() {
			if (this.suppressSync) return;
			if (this.selectedStructureType !== SIMPLE_STRUCTURE_TYPE.PIPE) return;
			this.suppressSync = true;
			const p1 = this.formData.pipe.points.p1;
			const p2 = this.formData.pipe.points.p2;
			const { gradientValue, length } = computePipeGradientFromPoints(p1, p2);
			this.formData.pipe.gradient.value = gradientValue;
			this.formData.pipe.length = length;
			this.$nextTick(() => {
				this.suppressSync = false;
			});
			if (this.tempMeshId) this.schedulePreviewUpdate();
		},
		"formData.pipe.points.p2.z"() {
			if (this.suppressSync) return;
			if (this.selectedStructureType !== SIMPLE_STRUCTURE_TYPE.PIPE) return;
			this.suppressSync = true;
			const p1 = this.formData.pipe.points.p1;
			const p2 = this.formData.pipe.points.p2;
			const { gradientValue, length } = computePipeGradientFromPoints(p1, p2);
			this.formData.pipe.gradient.value = gradientValue;
			this.formData.pipe.length = length;
			this.$nextTick(() => {
				this.suppressSync = false;
			});
			if (this.tempMeshId) this.schedulePreviewUpdate();
		},
		"formData.retainingWall.points.p1.z"(newZ) {
			if (this.suppressSync) return;
			if (this.selectedStructureType !== SIMPLE_STRUCTURE_TYPE.RETAINING_WALL)
				return;
			this.suppressSync = true;
			const z = Number(newZ);
			// Keep baseZ immutable (picked terrain Z) and derive wall height from top Z.
			const nextHeight = Math.max(0, z - this.retainingWallBaseZ.p1);
			this.formData.retainingWall.height = nextHeight;
			this.formData.retainingWall.points.p2 = {
				...this.formData.retainingWall.points.p2,
				z,
			};
			this.$nextTick(() => {
				this.suppressSync = false;
			});
			if (this.tempMeshId) this.schedulePreviewUpdate();
		},
		"formData.retainingWall.points.p2.z"(newZ) {
			if (this.suppressSync) return;
			if (this.selectedStructureType !== SIMPLE_STRUCTURE_TYPE.RETAINING_WALL)
				return;
			this.suppressSync = true;
			const z = Number(newZ);
			// Keep baseZ immutable (picked terrain Z) and derive wall height from top Z.
			const nextHeight = Math.max(0, z - this.retainingWallBaseZ.p2);
			this.formData.retainingWall.height = nextHeight;
			this.formData.retainingWall.points.p1 = {
				...this.formData.retainingWall.points.p1,
				z,
			};
			this.$nextTick(() => {
				this.suppressSync = false;
			});
			if (this.tempMeshId) this.schedulePreviewUpdate();
		},
		"formData.retainingWall.points": {
			handler() {
				if (this.suppressSync) return;
				this.suppressSync = true;
				const p1 = this.formData.retainingWall.points.p1;
				const p2 = this.formData.retainingWall.points.p2;
				const hdx = p2.x - p1.x;
				const hdy = p2.y - p1.y;
				const horiz = Math.sqrt(hdx * hdx + hdy * hdy);
				this.formData.retainingWall.width = horiz;
				this.suppressSync = false;
			},
			deep: true,
		},
		"formData.retainingWall.width"(newVal) {
			if (this.suppressSync) return;
			this.suppressSync = true;
			const parsedWidth = toOptionalNumber(newVal, { emptyValue: 0 });
			const p1 = this.formData.retainingWall.points.p1;
			const p2 = this.formData.retainingWall.points.p2;
			const newW = parsedWidth ?? 0;
			const newP2 = scaleSegmentToLength(p1, p2, newW, "box");
			this.formData.retainingWall.points.p2 = newP2;
			this.$nextTick(() => {
				this.suppressSync = false;
			});
			if (this.tempMeshId) this.schedulePreviewUpdate();
		},
		"formData.retainingWall.height"(newVal) {
			if (this.suppressSync) return;
			if (this.selectedStructureType !== SIMPLE_STRUCTURE_TYPE.RETAINING_WALL)
				return;
			// Height adds to the base (terrain) Z — update form points Z.
			this.suppressSync = true;
			const parsedHeight = toOptionalNumber(newVal, { emptyValue: 0 });
			const h = parsedHeight ?? 0;
			this.formData.retainingWall.points.p1 = {
				...this.formData.retainingWall.points.p1,
				z: this.retainingWallBaseZ.p1 + h,
			};
			this.formData.retainingWall.points.p2 = {
				...this.formData.retainingWall.points.p2,
				z: this.retainingWallBaseZ.p2 + h,
			};
			this.$nextTick(() => {
				this.suppressSync = false;
			});
			if (this.tempMeshId) this.schedulePreviewUpdate();
		},
		initialTwoPoints: {
			handler(newVal) {
				if (!newVal || typeof newVal !== "object") return;
				const p1 = this.toPoint3(newVal.p1);
				const p2 = this.toPoint3(newVal.p2);
				this.suppressSync = true;
				switch (this.selectedStructureType) {
					case SIMPLE_STRUCTURE_TYPE.PIPE:
						this.formData.pipe.points.p1 = p1;
						this.formData.pipe.points.p2 = p2;
						{
							const { gradientValue } = computePipeGradientFromPoints(p1, p2);
							this.formData.pipe.gradient.value = gradientValue;
						}
						break;
					case SIMPLE_STRUCTURE_TYPE.RETAINING_WALL:
						{
							// p1/p2 are the picked terrain positions.
							// Store their Z as the base (terrain) reference.
							// Form points Z will be updated to baseZ + height
							// when the height watcher fires.
							this.retainingWallBaseZ = { p1: p1.z, p2: p2.z };
							this.formData.retainingWall.points.p1 = p1;
							this.formData.retainingWall.points.p2 = p2;
						}
						break;
					default:
						break;
				}
				this.suppressSync = false;
				if (this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.PIPE) {
					const ldx = p2.x - p1.x;
					const ldy = p2.y - p1.y;
					const ldz = p2.z - p1.z;
					const len = Math.sqrt(ldx * ldx + ldy * ldy + ldz * ldz);
					this.formData.pipe.length = len;
				} else if (
					this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL
				) {
					const hdx = p2.x - p1.x;
					const hdy = p2.y - p1.y;
					const horiz = Math.sqrt(hdx * hdx + hdy * hdy);
					this.formData.retainingWall.width = horiz;
				}
			},
			immediate: true,
			deep: true,
		},
		"formData.pipe": {
			handler() {
				if (this.suppressSync) return;
				if (
					this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.PIPE &&
					this.tempMeshId
				) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		"formData.retainingWall": {
			handler() {
				if (this.suppressSync) return;
				if (
					this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL &&
					this.tempMeshId
				) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		"formData.color": {
			handler() {
				if (this.suppressSync) return;
				if (this.tempMeshId) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		tempMeshId: {
			async handler(newMeshId) {
				if (!newMeshId) return;
				if (this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.PIPE) {
					this.suppressSync = true;
					this.suppressSync = false;
					await this.updateTempMeshPreview();
				} else if (
					this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL
				) {
					this.suppressSync = true;
					try {
						if (!this.formData.retainingWall.height) {
							this.formData.retainingWall.height =
								this.creationDefaults().retainingWallHeight;
						}
						// Sync form points Z = baseZ + height so preview extends upward
						const h = Number(this.formData.retainingWall.height) || 0;
						this.formData.retainingWall.points.p1 = {
							...this.formData.retainingWall.points.p1,
							z: this.retainingWallBaseZ.p1 + h,
						};
						this.formData.retainingWall.points.p2 = {
							...this.formData.retainingWall.points.p2,
							z: this.retainingWallBaseZ.p2 + h,
						};
					} catch (error) {
						console.error(
							"Failed to set initial retaining-wall values:",
							error,
						);
					}
					this.suppressSync = false;
					await this.updateTempMeshPreview();
				}
			},
			immediate: true,
		},
	},
	mounted() {
		this.localDialogOpen = true;
		if (!this.initialTwoPoints && !this.tempMeshId) {
			this.resetFormData();
		}
	},
	methods: {
		/**
		 * Creation defaults for the site distance unit.
		 * Metric keeps the existing values; feet sites use 1 ft / 3 ft.
		 */
		creationDefaults() {
			if (this.isMetricDistance) return METRIC_CREATION_DEFAULTS;
			return {
				pipeDiameter: this.convertLength(1, "site", "mm"),
				retainingWallHeight: this.convertLength(3, "site", "m"),
			};
		},
		resolveMaxDecimals(maxValue) {
			return getMaxDecimals(maxValue);
		},
		isNonEmptyFiniteNumber(v) {
			return v !== "" && v !== null && v !== undefined && Number.isFinite(v);
		},
		hasAllowedDecimals(value, maxValue) {
			if (value === null || value === undefined) return false;
			const normalizedValue = String(value).trim();
			if (normalizedValue === "") return false;
			if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(normalizedValue)) {
				return false;
			}
			const decimals = normalizedValue.split(".")[1]?.length ?? 0;
			return decimals <= getMaxDecimals(maxValue);
		},
		onPipeGradientDisplayValueChange(displayValue) {
			this.gradientDisplayValues.pipe = String(displayValue ?? "");
		},
		onRetainingWallGradientDisplayValueChange(displayValue) {
			this.gradientDisplayValues.retainingWall = String(displayValue ?? "");
		},
		resetFormData() {
			this.suppressSync = true;
			this.formData = createInitialFormData(this.creationDefaults());
			this.gradientDisplayValues = {
				pipe: "0",
				retainingWall: "0",
			};
			this.suppressSync = false;
		},
		getPointsKey() {
			switch (this.selectedStructureType) {
				case SIMPLE_STRUCTURE_TYPE.PIPE:
					return SIMPLE_STRUCTURE_TYPE.PIPE;
				case SIMPLE_STRUCTURE_TYPE.RETAINING_WALL:
					return "retainingWall";
				default:
					return null;
			}
		},
		getBasisPointKey(index) {
			const cfg = SIMPLE_STRUCTURE_TYPES.find(
				(t) => t.id === this.selectedStructureType,
			);
			if (cfg) {
				if (index === 1 && cfg.basisPoint1Key) return cfg.basisPoint1Key;
				if (index === 2 && cfg.basisPoint2Key) return cfg.basisPoint2Key;
			}
			return index === 1 ? "BASIS_POINT_1" : "BASIS_POINT_2";
		},
		getSuffix(unitType) {
			const unit = UnitTypes.find((type) => type.value === unitType);
			return unit ? unit.symbol : unitType;
		},
		getTitleKey() {
			const keyMap = {
				[SIMPLE_STRUCTURE_TYPE.PIPE]: "ADD_PIPE",
				[SIMPLE_STRUCTURE_TYPE.RETAINING_WALL]: "ADD_RETAINING_WALL",
			};
			const key = keyMap[this.selectedStructureType];
			return key ? this.$t(key) : "";
		},
		roundTo3,
		roundPointTo3,
		/** Normalizes a picked point without rounding, defaulting missing axes to 0. */
		toPoint3(p) {
			return { x: p?.x ?? 0, y: p?.y ?? 0, z: p?.z ?? 0 };
		},
		rgbToHex(rgb) {
			if (!rgb) return "#ffffff";
			const r = Math.round((rgb.r ?? 0) * 255);
			const g = Math.round((rgb.g ?? 0) * 255);
			const b = Math.round((rgb.b ?? 0) * 255);
			return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
		},
		onPickerColorChange(val) {
			if (typeof val === "object" && val !== null) {
				this.formData.color = {
					r: (val.r ?? 0) / 255,
					g: (val.g ?? 0) / 255,
					b: (val.b ?? 0) / 255,
					a: 1,
				};
			}
		},
		buildViewerParams() {
			switch (this.selectedStructureType) {
				case SIMPLE_STRUCTURE_TYPE.PIPE:
					return buildPipeFromTwoPoints({
						P1: this.formData.pipe.points.p1,
						P2: this.formData.pipe.points.p2,
						diameter_mm: Number(this.formData.pipe.diameter) || 0,
					});
				case SIMPLE_STRUCTURE_TYPE.RETAINING_WALL: {
					const gv = Number(this.formData.retainingWall.gradient?.value);
					const suffix = this.getSuffix(
						this.$store.state.roadParameterSlopeUnit.longitudinalSlope,
					);
					const gradient_percent = slopeValueToGradientPercent(gv, suffix);
					return buildRetainingWallFromTwoPoints({
						P1: this.formData.retainingWall.points.p1,
						P2: this.formData.retainingWall.points.p2,
						height_m: Number(this.formData.retainingWall.height) || 0,
						gradient_percent,
						thickness_m: Number(this.formData.retainingWall.thickness) || 0.5,
					});
				}
				default:
					return undefined;
			}
		},
		onPipeGradientChange(viewerVal) {
			if (this.suppressSync) return;
			const gv = Number(viewerVal);
			const suffix = this.getSuffix(
				this.$store.state.roadParameterSlopeUnit.longitudinalSlope,
			);
			const gradientPercent = slopeValueToGradientPercent(gv, suffix);
			const p1 = this.formData.pipe.points.p1;
			const p2 = this.formData.pipe.points.p2;
			const tdx = p2.x - p1.x;
			const tdy = p2.y - p1.y;
			const tdz = p2.z - p1.z;
			const targetLen =
				Number(this.formData.pipe.length) ||
				Math.sqrt(tdx * tdx + tdy * tdy + tdz * tdz);
			if (targetLen < 1e-10) return;
			this.suppressSync = true;
			this.formData.pipe.points.p2 = computePipeP2FromGradient(
				p1,
				p2,
				gradientPercent,
				targetLen,
			);
			this.$nextTick(() => {
				this.suppressSync = false;
			});
			if (this.tempMeshId) this.schedulePreviewUpdate();
		},
		schedulePreviewUpdate() {
			if (this.previewUpdateTimer) {
				clearTimeout(this.previewUpdateTimer);
			}
			this.previewUpdateTimer = setTimeout(() => {
				this.updateTempMeshPreview();
			}, 300);
		},
		async updateTempMeshPreview() {
			const type = this.selectedStructureType;
			if (
				!this.tempMeshId ||
				(type !== SIMPLE_STRUCTURE_TYPE.PIPE &&
					type !== SIMPLE_STRUCTURE_TYPE.RETAINING_WALL)
			)
				return;
			try {
				const computed = this.buildViewerParams();
				if (!computed) return;
				const d = computed.dimensions;
				if (!d.x || !d.y || !d.z) return;
				// Update bottomZ before applyPreviewParams so enforceAnchorConstraint
				// uses the correct value for the new height.
				if (computed.meta?.bottomZ != null) {
					await this.$store.dispatch("solidMesh/updateSolidMeshAttributes", {
						meshId: this.tempMeshId,
						attributes: { bottomZ: computed.meta.bottomZ },
					});
				}
				await this.$store.dispatch("solidMesh/applyPreviewParams", {
					meshId: this.tempMeshId,
					params: {
						dimensions: computed.dimensions,
						localCenter: computed.center,
						hprRad: {
							heading: computed.rotation.z,
							pitch: computed.rotation.y,
							roll: computed.rotation.x,
						},
					},
				});
				await this.$store.dispatch("solidMesh/setSolidMeshColor", {
					meshId: this.tempMeshId,
					colorArr: [
						this.formData.color?.r ?? 1,
						this.formData.color?.g ?? 1,
						this.formData.color?.b ?? 1,
					],
				});
				await this.$store.dispatch("solidMesh/setSolidMeshVisibility", {
					meshId: this.tempMeshId,
					visible: true,
				});
			} catch (error) {
				console.error("Failed to update mesh preview:", error);
			}
		},
		confirm() {
			const key = this.getPointsKey();
			const params = Object.assign({}, this.formData[key]);

			if (this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.PIPE) {
				params.diameter = Number(params.diameter) || 0;
				params.length = Number(params.length) || 0;
			}
			if (this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
				params.width = Number(params.width) || 0;
				params.height = Number(params.height) || 0;
				params.thickness = Number(params.thickness) || 0;
			}

			const payload = {
				structureType: this.selectedStructureType,
				parameters: params,
				name: this.formData.name,
				color: {
					red: this.formData.color?.r ?? 1,
					green: this.formData.color?.g ?? 1,
					blue: this.formData.color?.b ?? 1,
					alpha: this.formData.color?.a ?? 1,
				},
			};
			const computed = this.buildViewerParams();
			if (computed) payload.viewerParams = computed;

			this.$emit("confirm", payload);
			const confirmMap = {
				[SIMPLE_STRUCTURE_TYPE.PIPE]:
					GoogleAnalyticsClickTypes.P_CLICK_ADD_SIMPLE_STRUCTURE_PIPE,
				[SIMPLE_STRUCTURE_TYPE.RETAINING_WALL]:
					GoogleAnalyticsClickTypes.P_CLICK_ADD_SIMPLE_STRUCTURE_RETAINING_WALL,
			};
			const ct = confirmMap[this.selectedStructureType];
			if (ct) addClickTypeLog(ct);
			this.localDialogOpen = false;
			this.$emit("update:modelValue", false);
		},
		async cancel() {
			if (this.tempMeshId) {
				try {
					await this.$store.dispatch("solidMesh/deleteSolidMesh", {
						meshId: this.tempMeshId,
					});
				} catch (error) {
					console.error("Failed to delete temp mesh on cancel:", error);
				}
			}
			this.$emit("cancel");
			this.localDialogOpen = false;
			this.$emit("update:modelValue", false);
		},
	},
	beforeUnmount() {
		if (this.previewUpdateTimer) {
			clearTimeout(this.previewUpdateTimer);
			this.previewUpdateTimer = null;
		}
	},
};
</script>

<style scoped>
.section-title {
	font-weight: 600;
	font-size: 0.95rem;
	color: rgba(255, 255, 255, 0.87);
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	padding-bottom: 8px;
}
</style>
